import React from "react"
import {
  Alert,
  Box,
  Divider,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material"
import { useFormikContext } from "formik"
import { useTranslation } from "react-i18next"
import { StyledHeaderTableCell, StyledSummarySection, StyledTableCell } from "../CreateTemplateModal.styled"
import { TemplateFormData, Vehicle } from "../CreateTemplateModal.interfaces"
import { StyledRowGapBox } from "shared/styled/General"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons"
import VehicleSwitch from "../VehicleSwitch"

interface SummaryStepProps {
  vehicles: Vehicle[]
  showInternalNumber: boolean
  sessionServiceValues: any
  originalServiceValues: any
  vehicleInspectionData: { [vehicleId: number]: any }
  setShowInternalNumber: React.Dispatch<React.SetStateAction<boolean>>
}

const SummaryStep: React.FC<SummaryStepProps> = ({
  vehicles,
  showInternalNumber,
  sessionServiceValues,
  originalServiceValues,
  vehicleInspectionData,
  setShowInternalNumber
}) => {
  const { t } = useTranslation()
  const formik = useFormikContext<TemplateFormData>()
  const theme = useTheme()

  return (
    <Box>
      <StyledRowGapBox justifyContent="space-between" mb={3} sx={{ gap: 1 }} flexWrap="wrap">
        <StyledRowGapBox>
          <Typography variant="h3">
            {t("global.summary", "Summary")}
          </Typography>
          <Tooltip title=
            {t("checks.review_template_settings", "Review the information before creating the template. When the template is saved, these settings are used to plan future checks.")}>
            <FontAwesomeIcon
              icon={faInfoCircle}
              color={(theme.palette as any).primary[700]}
              size="lg"
            />
          </Tooltip>
        </StyledRowGapBox>
        <VehicleSwitch setShowValue={setShowInternalNumber} showValue={showInternalNumber} />
      </StyledRowGapBox>

      <StyledSummarySection>
        <Typography variant="inputLabel" fontWeight={400} color="textSecondary">
          {t("global.name", "Name")}
        </Typography>
        <Typography variant="bodyMedium">
          {formik.values.name}
        </Typography>
      </StyledSummarySection>

      <Divider sx={{ my: 2.5 }} />

      <StyledSummarySection>
        <Typography variant="inputLabel" fontWeight={400} color="textSecondary">
          {t("global.checktype", "Check type")}
        </Typography>
        {formik.values.usesDate && (
          <Typography variant="bodyMedium">
            {t("global.date_based", "Date based")} - {t("global.per", "per")} {formik.values.dateInterval} {t("global.day_lower", "day")} ({formik.values.dateCalculationType === "Dynamic" ? t("global.dynamic", "Dynamic") : t("global.static", "Static")})
          </Typography>
        )}
        {formik.values.usesDistance && (
          <Typography variant="bodyMedium">
            {t("global.distance_based", "Distance based")} - {t("global.per", "per")} {formik.values.distanceInterval} km ({formik.values.distanceCalculationType === "Dynamic" ? t("global.dynamic", "Dynamic") : t("global.static", "Static")})
          </Typography>
        )}
        {formik.values.usesInspection && (
          <Typography variant="bodyMedium">
            {t("global.inspection_based", "Inspection based")} - {t("checks.calculation_transportstyrelsen", "Calculated based on the next inspection date from the Swedish Transport Agency.")}
          </Typography>
        )}
      </StyledSummarySection>

      <Divider sx={{ my: 2.5 }} />

      <StyledSummarySection>
        <StyledRowGapBox sx={{ gap: 1 }}>
          <Typography variant="inputLabel" fontWeight={400} color="textSecondary">
            {t("global.selected_vehicles_amount", "Number of selected vehicles")}:
          </Typography>
          <Typography fontWeight="600">{formik.values.selectedVehicles.length}</Typography>
        </StyledRowGapBox>

        <TableContainer sx={{ mt: 1 }}>
          <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableHead>
              <TableRow>
                <StyledHeaderTableCell>{showInternalNumber ? t("global.internal_number", "Internal number") : t("deviation.license_plate", "License plate")}</StyledHeaderTableCell>
                {formik.values.usesDate && <StyledHeaderTableCell>{t("global.latest_date", "Latest date")}</StyledHeaderTableCell>}
                {formik.values.usesDistance && <StyledHeaderTableCell>{t("global.latest_metersetting", "Latest meter reading")}</StyledHeaderTableCell>}
                {formik.values.usesInspection && <StyledHeaderTableCell>{t("global.inspection_data", "Inspection data")}</StyledHeaderTableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {vehicles
                .filter(v => formik.values.selectedVehicles.includes(v.id))
                .map((vehicle) => {
                  // Use session values if available (for re-checked vehicles),
                  // otherwise use form values, otherwise use original values
                  const lastValues = formik.values.vehicleLastValues[vehicle.id] ||
                    sessionServiceValues[vehicle.id] ||
                    originalServiceValues[vehicle.id] || {}
                  return (
                    <TableRow key={vehicle.id}>
                      <StyledTableCell>
                        {showInternalNumber
                          ? (vehicle.internnumber || vehicle.regNumber)
                          : vehicle.regNumber
                        }
                      </StyledTableCell>
                      {formik.values.usesDate && (
                        <StyledTableCell>
                          {lastValues.lastDate
                            ? new Date(lastValues.lastDate).toLocaleDateString("sv-SE")
                            : "-"
                          }
                        </StyledTableCell>
                      )}
                      {formik.values.usesDistance && (
                        <StyledTableCell>{lastValues.lastDistance || "-"}</StyledTableCell>
                      )}
                      {formik.values.usesInspection && (
                        <StyledTableCell>
                          <StyledRowGapBox sx={{ gap: 0.5 }}>
                            {vehicleInspectionData[vehicle.id]?.lastInspectionDate
                              ? new Date(vehicleInspectionData[vehicle.id].lastInspectionDate).toLocaleDateString("sv-SE")
                              : t("global.retrieved_automatically", "Retrieved automatically")}
                            {vehicleInspectionData[vehicle.id]?.inspectionDeadline && (
                              <Typography variant="caption" color="text.secondary">
                                ({t("global.buttons.next", "Next")}: {new Date(vehicleInspectionData[vehicle.id].inspectionDeadline).toLocaleDateString("sv-SE")})
                              </Typography>
                            )}
                          </StyledRowGapBox>
                        </StyledTableCell>
                      )}
                    </TableRow>
                  )
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </StyledSummarySection>
    </Box>
  )
}

export default SummaryStep
